import {useMultiStyleConfig, BoxProps, StackProps, FlexProps, TextProps, VStack, Box, Flex} from '@chakra-ui/react'
import {IconProps, CtaButton, ScrollDownButton, RichText, CustomStyle, Icon} from '_uiComponents'
import {useAnalytics, useIsElementInViewport, useOpenDrawer, useAccordionTrigger} from '_customHooks'
import React, {useEffect, useRef, useState} from 'react'
import {PAGE_DESIGNER_MODULES} from '_utils'
import {useIsInsideAccordion} from '../accordion-pd'

interface TitleTextCtaProps {
    eyelet?: string
    title?: string
    description?: string

    ctaText?: string
    ctaUrl?: string
    ctaTargetBlank?: boolean

    cta2Text?: string
    cta2Url?: string
    cta2TargetBlank?: boolean

    ctaStyle?: 'text' | 'button'
    fullHeight?: boolean
    textAlign?: 'left' | 'center' | 'right'
    horizontalPosition?: 'left' | 'center' | 'right'
    contentColor?: string
    contentBackground?: string

    scrollDownArrow?: boolean
    ctaTextAnimation?: boolean
    textAnimation?: boolean

    classes?: string
    extraCSS?: string
    analyticsList?: string
    position: number
    analyticsID?: string
    analyticsName?: string
}

/**
 * TitleTextCta component renders a text section that can display an eyelet, title, description, one or two CTAs, and a scroll down arrow.
 *
 * @param {string} [eyelet] - Optional short label or eyelet displayed above the title.
 * @param {string} [title] - The main title text to display.
 * @param {string} [description] - Optional description text below the title.
 * @param {string} [ctaText] - Text for the primary CTA button.
 * @param {string} [ctaUrl] - URL for the primary CTA button.
 * @param {boolean} [ctaTargetBlank] - If true, opens the primary CTA link in a new tab.
 * @param {string} [cta2Text] - Text for the secondary CTA button.
 * @param {string} [cta2Url] - URL for the secondary CTA button.
 * @param {boolean} [cta2TargetBlank] - If true, opens the secondary CTA link in a new tab.
 * @param {'text' | 'button'} [ctaStyle] - Visual style variant for the CTA buttons.
 * @param {boolean} [fullHeight] - If true the component takes the full height of its container.
 * @param {'left' | 'center' | 'right'} [textAlign] - Text alignment for the content.
 * @param {'left' | 'center' | 'right'} [horizontalPosition] - Horizontal positioning of the content.
 * @param {string} [contentColor] - The content text color.
 * @param {string} [contentBackground] - The content background color.
 * @param {boolean} [scrollDownArrow] - If true, displays a scroll down arrow button.
 * @param {boolean} [ctaTextAnimation] - If true, enables animation for CTA button text.
 * @param {boolean} [textAnimation] - If true, enables animation for title text.
 * @param {string} [classes] - Additional CSS classes to apply to the component.
 * @param {string} [extraCSS] - Additional CSS styles to apply to the component.
 * @param {number} [position] - The position of the element with respect to other page designer elements in the page.
 * @param {string} props.analyticsID - The analytics id to send for the tracking
 *
 * @returns {JSX.Element} The rendered TitleTextCta component.
 */
const TitleTextCta = ({
    eyelet,
    title,
    description,
    ctaText,
    ctaUrl,
    ctaTargetBlank,
    cta2Text,
    cta2Url,
    cta2TargetBlank,
    ctaStyle,
    fullHeight,
    textAlign,
    horizontalPosition,
    contentColor = 'color.text.black',
    contentBackground = 'color.text.white',
    scrollDownArrow,
    ctaTextAnimation,
    textAnimation,
    classes,
    extraCSS,
    position,
    analyticsID,
    analyticsName,
}: TitleTextCtaProps): JSX.Element => {
    const ctaNum = Number(Boolean(ctaText && ctaUrl)) + Number(Boolean(cta2Text && cta2Url))
    // Inside a 32_accordion the text goes full width on mobile (the accordion panel insets 24px per side),
    // and stays at the standalone 65% from md up, as SFRA `.cc-content-text` does.
    const insideAccordion = useIsInsideAccordion()
    // When a CTA targets a 32_accordion module, show a chevron that reflects its open/closed state.
    const cta1Accordion = useAccordionTrigger(ctaUrl)
    const cta2Accordion = useAccordionTrigger(cta2Url)
    // The trigger module follows the Figma "32 - Accordion" CTA (button-sm, 40px below the text), not the standalone module 12.
    const accordionTrigger = cta1Accordion.isAccordionTrigger || cta2Accordion.isAccordionTrigger
    const styles = useMultiStyleConfig('TitleTextCta', {
        textAlign,
        horizontalPosition,
        contentColor,
        contentBackground,
        textAnimation,
        fullHeight,
        ctaNum,
        insideAccordion,
        accordionTrigger,
        hasHeading: Boolean(eyelet || title),
    }) as Record<string, BoxProps & StackProps & FlexProps & TextProps & IconProps>

    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const {handleShopTheImageClick, handleAccordionClick} = useOpenDrawer()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const [alreadySent, setAlreadySent] = useState(false)
    const animationTime = '1000'

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.TITLE_TEXT_CTA,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    useEffect(() => {
        if (isVisible && !alreadySent) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.TITLE_TEXT_CTA,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySent(true)
        }
    }, [isVisible])

    return (
        <Box {...styles.wrapper} data-testid="title-text-cta" ref={targetElement}>
            <CustomStyle css={extraCSS} />
            <VStack {...styles.container} className={`${classes ?? ''} cc-pd cc-pd-title-text-cta`}>
                {eyelet && <RichText {...styles.eyelet} className="cc-eyelet" content={eyelet} />}
                {title && <RichText as="h2" {...styles.title} className="cc-title" content={title} />}
                {description && <RichText {...styles.text} className="cc-description" content={description} />}

                {((ctaText && ctaUrl) || (cta2Text && cta2Url)) && (
                    <Flex {...styles.ctaContainer} className="cc-content-buttons" data-testid="title-text-ctas">
                        {ctaText && ctaUrl && (
                            <CtaButton
                                variant={ctaStyle}
                                href={ctaUrl}
                                size={cta1Accordion.isAccordionTrigger ? 'sm' : undefined}
                                isAnimated={ctaTextAnimation}
                                animationTime={animationTime}
                                openInNewTab={ctaTargetBlank}
                                ctaColor={contentColor}
                                onClick={(e) => {
                                    if (handleShopTheImageClick(e, ctaUrl)) return
                                    if (handleAccordionClick(e, ctaUrl)) return
                                    triggerAnalyticsEventPromoClick(ctaText, cta2Text && cta2Url ? '1' : '')
                                }}
                            >
                                {ctaText}
                                {cta1Accordion.isAccordionTrigger && (
                                    <Icon
                                        name={cta1Accordion.isOpen ? 'IconChevronUp' : 'IconChevronDown'}
                                        size="xs"
                                        aria-hidden
                                        sx={styles.accordionChevron}
                                    />
                                )}
                            </CtaButton>
                        )}
                        {cta2Text && cta2Url && (
                            <CtaButton
                                variant={ctaStyle}
                                href={cta2Url}
                                size={cta2Accordion.isAccordionTrigger ? 'sm' : undefined}
                                isAnimated={ctaTextAnimation}
                                animationTime={animationTime}
                                openInNewTab={cta2TargetBlank}
                                ctaColor={contentColor}
                                onClick={(e) => {
                                    if (handleShopTheImageClick(e, cta2Url)) return
                                    if (handleAccordionClick(e, cta2Url)) return
                                    triggerAnalyticsEventPromoClick(cta2Text, ctaText && ctaUrl ? '2' : '')
                                }}
                            >
                                {cta2Text}
                                {cta2Accordion.isAccordionTrigger && (
                                    <Icon
                                        name={cta2Accordion.isOpen ? 'IconChevronUp' : 'IconChevronDown'}
                                        size="xs"
                                        aria-hidden
                                        sx={styles.accordionChevron}
                                    />
                                )}
                            </CtaButton>
                        )}
                    </Flex>
                )}

                {scrollDownArrow && (
                    <ScrollDownButton parentDataTestId="title-text-cta" dataTestId="title-text-cta-scroll-down" ariaLabel="Button scroll down title text cta" />
                )}
            </VStack>
        </Box>
    )
}

export {TitleTextCta}
